import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck } from "lucide-react";

const inputCls =
  "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      {sent ? (
        <div data-testid="forgot-success">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E30613]/15 text-[#FF1C2A]">
            <MailCheck className="h-6 w-6" />
          </span>
          <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-white">Check your email</h1>
          <p className="mt-3 text-sm leading-relaxed text-neutral-400">
            If an account exists for <span className="text-white">{email}</span>, we've sent a link to reset
            your password. The link expires in one hour.
          </p>
          <Link to="/login" className="mt-8 inline-block text-sm font-medium text-white transition-colors hover:text-[#FF1C2A]">
            ← Back to sign in
          </Link>
        </div>
      ) : (
        <>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Account Recovery</p>
          <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white">Forgot password</h1>
          <p className="mt-2 text-sm text-neutral-500">Enter your business email and we'll send a reset link.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
            {error && (
              <div className="rounded-lg border border-[#E30613]/30 bg-[#E30613]/10 px-4 py-3 text-sm text-[#FF6069]">{error}</div>
            )}
            <div className="space-y-2">
              <Label className="text-neutral-300">Business Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} data-testid="forgot-email" />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex h-11 w-full items-center justify-center rounded-lg bg-[#E30613] font-medium text-white transition-colors duration-200 hover:bg-[#FF1C2A] disabled:opacity-60"
              data-testid="forgot-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Reset Link"}
            </button>
          </form>

          <Link to="/login" className="mt-8 inline-block text-sm text-neutral-500 transition-colors hover:text-white">
            ← Back to sign in
          </Link>
        </>
      )}
    </AuthShell>
  );
}
